/**
 * lib/leaveCardPdf.ts
 * ------------------------------------------------------------------
 * HR LEAVE — print the yearly leave card (lib/leaveCard) in the layout HR
 * already uses ("TEACHERS LEAVE 2026 final.xls", one sheet per teacher):
 * the name and year, the brought-forward line (days taken, Dec balance,
 * holiday days, sold, C/F), the month table (leave per month, leave taken,
 * sick leave, maternity when there is any, balance, notes), the totals, and
 * the signature block — Employee, Approved by, Designation, Date, Prepared
 * by, From. A4 portrait, one page per person; many cards print as one PDF.
 * ------------------------------------------------------------------ */
import { jsPDF } from 'jspdf';
import { SCHOOL } from '@/lib/school.config';
import type { LeaveCard } from '@/lib/leaveCard';

export interface LeaveCardPage {
  name: string;
  card: LeaveCard;
}

export interface LeaveCardSign {
  approvedBy: string;
  designation: string;
  preparedBy: string;
}

const fmt = (n: number | null | undefined) => (n === null || n === undefined ? '' : (Math.round(n * 100) / 100).toFixed(2));

function renderPage(pdf: jsPDF, p: LeaveCardPage, sign: LeaveCardSign, logo?: string | null): void {
  const MX = 18;
  const W = 210 - MX * 2;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [156, 163, 175];
  const FILL: [number, number, number] = [239, 243, 249];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  let y = 16;

  // Letterhead.
  if (logo) { try { pdf.addImage(logo, 'PNG', MX, y - 4, 14, 14); } catch { /* ignore */ } }
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12);
  pdf.text(SCHOOL.name, MX + 17, y + 1);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text('Staff leave card', MX + 17, y + 6);
  y += 16;

  // Name and year.
  setT(INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13);
  pdf.text(p.name.toUpperCase(), MX, y);
  pdf.text(String(p.card.year), MX + W, y, { align: 'right' });
  y += 8;

  // A ruled table: columns as [label, width, align].
  const table = (cols: [string, number, 'left' | 'right'][], rows: string[][], opts: { head?: boolean; boldLast?: boolean } = {}) => {
    const h = 7;
    const drawRow = (cells: string[], bold: boolean, fill: boolean) => {
      let x = MX;
      if (fill) { pdf.setFillColor(FILL[0], FILL[1], FILL[2]); pdf.rect(MX, y, W, h, 'F'); }
      pdf.setDrawColor(RULE[0], RULE[1], RULE[2]); pdf.setLineWidth(0.2);
      pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(9); setT(INK);
      cols.forEach(([, w, align], i) => {
        pdf.rect(x, y, w, h);
        const text = pdf.splitTextToSize(cells[i] ?? '', w - 3)[0] ?? '';
        pdf.text(text, align === 'right' ? x + w - 1.5 : x + 1.5, y + 4.8, { align });
        x += w;
      });
      y += h;
    };
    if (opts.head !== false) drawRow(cols.map(([l]) => l), true, true);
    rows.forEach((r, i) => drawRow(r, !!opts.boldLast && i === rows.length - 1, false));
  };

  // Brought forward into the year.
  const hd = p.card.header;
  const head: [string, number, 'left' | 'right'][] = [['Name', W - 5 * 26, 'left'], ['Days taken', 26, 'right'], ["Dec bal '" + String(p.card.year - 1).slice(2), 26, 'right'], ['Hol days', 26, 'right'], ['Sold', 26, 'right'], [`C/F ${p.card.year}`, 26, 'right']];
  table(head, [[p.name, fmt(p.card.totals.taken), hd ? fmt(hd.decBal25) : '', hd ? fmt(hd.holDays) : '', hd ? fmt(hd.sold) : '', hd ? fmt(hd.cf2026) : '']]);
  y += 6;

  // The months.
  const mat = p.card.hasMaternity;
  const numW = 21;
  const cols: [string, number, 'left' | 'right'][] = [
    ['Month', 18, 'left'], ['Leave/mth', numW, 'right'], ['Leave taken', numW, 'right'], ['Sick leave', numW, 'right'],
    ...(mat ? [['Maternity', numW, 'right'] as [string, number, 'left' | 'right']] : []),
    ['Bal leave', numW, 'right'],
  ];
  const used = cols.reduce((t, c) => t + c[1], 0);
  cols.push(['Notes', W - used, 'left']);
  const t = p.card.totals;
  table(cols, [
    ...p.card.rows.map((r) => [
      r.label, fmt(r.earned), r.taken ? fmt(r.taken) : '0', r.sick ? fmt(r.sick) : '0',
      ...(mat ? [r.maternity ? fmt(r.maternity) : '0'] : []), fmt(r.balance), r.notes,
    ]),
    ['Total', fmt(t.earned), fmt(t.taken), fmt(t.sick), ...(mat ? [fmt(t.maternity)] : []), fmt(p.card.balance), ''],
  ], { boldLast: true });
  y += 8;

  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); setT(INK);
  pdf.text('TOTAL LEAVE DAYS TAKEN', MX, y); pdf.text(fmt(t.taken), MX + 75, y, { align: 'right' }); y += 6;
  pdf.text('TOTAL SICK LEAVE DAYS', MX, y); pdf.text(fmt(t.sick), MX + 75, y, { align: 'right' }); y += 6;
  if (mat) { pdf.text('TOTAL MATERNITY LEAVE DAYS', MX, y); pdf.text(fmt(t.maternity), MX + 75, y, { align: 'right' }); y += 6; }
  y += 8;

  // Signatures, as the paper card has them.
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
  const line = (left: string, right?: string) => {
    pdf.text(left, MX, y);
    if (right) pdf.text(right, MX + W / 2 + 5, y);
    y += 10;
  };
  line(`Employee: ${p.name}`, 'Sign: ______________________________');
  line(`Approved by: ${sign.approvedBy}`, 'Sign: ______________________________');
  line(`Designation: ${sign.designation}`);
  line('Date: ______________________________');
  y += 4;
  pdf.setFont('helvetica', 'bold');
  line(`PREPARED BY: ${sign.preparedBy.toUpperCase()}`);
  y -= 4;
  line(`FROM: ${SCHOOL.name.toUpperCase()}`);
}

/** One PDF, a page per card. */
export function buildLeaveCardsPdf(pages: LeaveCardPage[], sign: LeaveCardSign, logo?: string | null): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  pages.forEach((p, i) => {
    if (i > 0) pdf.addPage();
    renderPage(pdf, p, sign, logo);
  });
  return pdf;
}
